import { AxiosRequestConfig, isAxiosError } from 'axios';
import { navigate } from 'gatsby';
import HttpClient from '../HttpClient';
import HTTPResponse from '../HttpResponse';
import { Errors } from './errors';
import { UserState } from '../../reducers/user/userTypes';
import {
  getNativeAppQueryParams,
  isNativeApp,
} from '../../helpers/nativeAppHelper';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
} from '../../helpers/urlHelper';

interface UserResponseData {
  firstName?: string;
  firstNamePhonetic?: string;
  lastName?: string;
  lastNamePhonetic?: string;
  states?: string;
  city?: string;
  streetAddress?: string;
  streetAddress2?: string;
  streetAddress3?: string;
  postalCode?: string;
  companyName?: string;
  email?: string;
  phoneNumber?: string;
  countryCode?: string;
  languageCode?: string;
  customerNumber?: string;
  type: 'Individual' | 'Business';
  allowedDiscounts?: boolean;
  integrityConsentMap?: {
    AcceptPrivacyPolicy?: boolean;
    AllowPersonalEmail?: boolean;
    AllowSalesProfileSharing?: boolean;
  };
}

const mapUser = (userResponseData: UserResponseData): UserState => {
  return {
    isLoggedIn: true,
    privacyPolicy: true,
    firstName: userResponseData.firstName,
    firstNamePhonetic: userResponseData.firstNamePhonetic,
    lastName: userResponseData.lastName,
    lastNamePhonetic: userResponseData.lastNamePhonetic,
    state: userResponseData.states,
    city: userResponseData.city || '',
    streetAddress: userResponseData.streetAddress || '',
    streetAddress2: userResponseData.streetAddress2,
    postCode: userResponseData.postalCode || '',
    businessName: userResponseData.companyName,
    email: userResponseData.email,
    phoneNumber: userResponseData.phoneNumber,
    country: userResponseData.countryCode || '',
    customerNumber: userResponseData.customerNumber,
    customerType: userResponseData.type,
    logInStatus: 'COMPLETE',
    allowedMemberDiscounts:
      userResponseData.allowedDiscounts ||
      userResponseData.type === 'Individual',
    allowPersonalEmail:
      userResponseData.integrityConsentMap?.AllowPersonalEmail,
  };
};

class UserService {
  private http: HttpClient;

  public constructor(http: HttpClient) {
    this.http = http;
  }

  public async getUserInformation(
    config: AxiosRequestConfig,
  ): Promise<HTTPResponse<UserState | null>> {
    const { testMode } = getNativeAppQueryParams();
    try {
      const response = await this.http.get<UserResponseData>(
        'customer',
        config,
      );

      const user = mapUser(response.data);

      return new HTTPResponse(user);
    } catch (error) {
      if (isNativeApp() && testMode !== 'true') {
        // redirect user if failed to retrieve nativeAccessToken or getUserInformation
        const path = getPagePath('service-unavailable');
        const pathWithQueryParams = addPersistingQueryParamsToPath(path);
        window.history.replaceState(null, '', pathWithQueryParams);
        navigate(pathWithQueryParams);
      }
      if (isAxiosError(error)) {
        const errorType =
          error.status && Object.values(Errors).indexOf(error.status) > -1
            ? error.status
            : Errors.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse(null, errorType);
      }
      return new HTTPResponse(null, Errors.SERVICE_NOT_AVAILABLE as number);
    }
  }
}

export default UserService;
